import type { CampoPropio, Etiqueta } from '../../../api/ops/clientes';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Fecha } from '../../../componentes/Fecha';
import { Interruptor } from '../../../componentes/Interruptor';
import { CASILLA_NACIMIENTO, IDIOMAS, N1_CAMPO, N2, N2_TITULO, hoyLocal } from '../lista/textos';
import type { DatosFormulario, ErroresFormulario } from './datos';

/**
 * Formulario de los datos de una ficha: el del alta (2.2) y el de «Datos» de la ficha (2.3), que solo cambia los datos de
 * tu negocio (nunca la Cuenta Reservaria, G35, G42). Instagram sin @ y en minúsculas (G147); fecha de nacimiento solo con
 * la casilla obligatoria (G241); campos propios de cada tipo; en el alta, etiquetas y la información comercial (N2, G162).
 */
interface Props {
    datos: DatosFormulario;
    onCambio: (d: DatosFormulario) => void;
    errores: ErroresFormulario;
    campos: readonly CampoPropio[];
    etiquetas: readonly Etiqueta[];
    /** Alta (2.2) o «Datos de tu negocio» de la ficha (2.3). */
    alta: boolean;
    /** Ficha vinculada a una Cuenta Reservaria (cambian las etiquetas y las ayudas). */
    vinculada?: boolean;
}

export function Formulario({ datos: d, onCambio, errores: e, campos, etiquetas, alta, vinculada = false }: Props) {
    const fijar = <K extends keyof DatosFormulario>(clave: K, valor: DatosFormulario[K]) => {
        onCambio({ ...d, [clave]: valor });
    };
    const fijarCampo = (clave: string, valor: unknown) => {
        onCambio({ ...d, campos: { ...d.campos, [clave]: valor } });
    };
    const ayudaTelefono = alta ? N1_CAMPO : vinculada ? 'Sin verificar. No cambia su cuenta.' : 'Sin verificar. Al verificarlo, se vincula.';
    const activos = campos.filter((c) => c.archived_at == null);
    const sencillos = activos.filter((c) => c.type !== 'boolean' && c.type !== 'multiselect');
    const siNo = activos.filter((c) => c.type === 'boolean');
    const varios = activos.filter((c) => c.type === 'multiselect');

    return (
        <div className="rv-cl-form">
            <h3 className="rv-cl-subtitulo">{alta ? 'Datos' : 'Datos de tu negocio'}</h3>
            {alta && <p className="rv-cl-nota">Basta con el nombre, el teléfono o el email.</p>}
            {!alta && vinculada && <p className="rv-cl-nota">Solo los ve tu negocio. Si los dejas vacíos, se usan los de su cuenta.</p>}
            <Campo
                etiqueta={!alta && vinculada ? 'Nombre en tu negocio' : 'Nombre y apellidos'}
                valor={d.nombre}
                onChange={(v) => {
                    fijar('nombre', v);
                }}
                error={e.nombre}
                ayuda={!alta && vinculada ? 'Por ejemplo, como le llamáis en sala.' : undefined}
                autocompletar="off"
                mayusculas="words"
                max={120}
                nombre="nombre"
            />
            <Campo etiqueta="Teléfono" tipo="tel" valor={d.telefono} onChange={(v) => { fijar('telefono', v); }} error={e.telefono} ayuda={ayudaTelefono} autocompletar="off" max={20} nombre="telefono" />
            <Campo etiqueta="Email" tipo="email" valor={d.email} onChange={(v) => { fijar('email', v); }} error={e.email} autocompletar="off" max={254} nombre="email" />
            <Campo etiqueta="Instagram" valor={d.instagram} onChange={(v) => { fijar('instagram', v); }} error={e.instagram} ayuda="Su usuario, sin @. Opcional." autocompletar="off" mayusculas="none" max={31} nombre="instagram" />
            <Desplegable etiqueta="Idioma de sus mensajes" valor={d.idioma} opciones={IDIOMAS} onChange={(v) => { fijar('idioma', v); }} nombre="idioma" />
            <Fecha
                etiqueta="Fecha de nacimiento"
                valor={d.nacimiento}
                max={hoyLocal()}
                onChange={(v) => {
                    onCambio({ ...d, nacimiento: v, consentimientoNacimiento: v === '' ? false : d.consentimientoNacimiento });
                }}
                error={e.nacimiento}
                ayuda="Opcional. Solo para felicitarle."
                nombre="nacimiento"
            />
            {d.nacimiento !== '' && (
                <Interruptor
                    etiqueta={CASILLA_NACIMIENTO}
                    valor={d.consentimientoNacimiento}
                    error={e.consentimiento}
                    onChange={(v) => {
                        fijar('consentimientoNacimiento', v);
                    }}
                />
            )}

            {activos.length > 0 && (
                <>
                    <h3 className="rv-cl-subtitulo">Campos propios</h3>
                    {sencillos.map((c) => {
                        const v = d.campos[c.key];
                        const texto = typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
                        const error = e[`campo:${c.key}`];
                        const ayuda = c.required ? 'Obligatorio.' : undefined;
                        if (c.type === 'select') {
                            return (
                                <Desplegable
                                    key={c.id}
                                    etiqueta={c.label}
                                    valor={texto}
                                    opciones={[{ valor: '', texto: 'Sin elegir' }, ...(c.options ?? []).map((o) => ({ valor: o, texto: o }))]}
                                    onChange={(x) => {
                                        fijarCampo(c.key, x);
                                    }}
                                    error={error}
                                    ayuda={ayuda}
                                />
                            );
                        }
                        if (c.type === 'date') {
                            return <Fecha key={c.id} etiqueta={c.label} valor={texto} onChange={(x) => { fijarCampo(c.key, x); }} error={error} ayuda={ayuda} />;
                        }
                        return <Campo key={c.id} etiqueta={c.label} tipo={c.type === 'number' ? 'number' : 'text'} valor={texto} onChange={(x) => { fijarCampo(c.key, x); }} error={error} ayuda={ayuda} autocompletar="off" />;
                    })}
                    {siNo.map((c) => (
                        <Interruptor key={c.id} etiqueta={c.label} ayuda={c.required ? 'Obligatorio: sí o no.' : undefined} valor={d.campos[c.key] === true} onChange={(x) => { fijarCampo(c.key, x); }} />
                    ))}
                    {varios.map((c) => {
                        const actual = Array.isArray(d.campos[c.key]) ? (d.campos[c.key] as string[]) : [];
                        return (
                            <div key={c.id} className="rv-cl-grupo">
                                <span className="rv-cl-grupo__titulo">{c.label}</span>
                                {(c.options ?? []).map((o) => (
                                    <Interruptor
                                        key={o}
                                        etiqueta={o}
                                        valor={actual.includes(o)}
                                        onChange={(on) => {
                                            fijarCampo(c.key, on ? [...actual.filter((x) => x !== o), o] : actual.filter((x) => x !== o));
                                        }}
                                    />
                                ))}
                                {e[`campo:${c.key}`] !== undefined && <span className="rv-cl-nota rv-cl-nota--error">{e[`campo:${c.key}`]}</span>}
                            </div>
                        );
                    })}
                </>
            )}

            {etiquetas.length > 0 && (
                <>
                    <h3 className="rv-cl-subtitulo">Etiquetas</h3>
                    {etiquetas.map((t) => (
                        <Interruptor
                            key={t.id}
                            etiqueta={t.name}
                            valor={d.etiquetas.includes(t.id)}
                            onChange={(on) => {
                                fijar('etiquetas', on ? [...d.etiquetas.filter((x) => x !== t.id), t.id] : d.etiquetas.filter((x) => x !== t.id));
                            }}
                        />
                    ))}
                </>
            )}

            {alta && (
                <>
                    <h3 className="rv-cl-subtitulo">{N2_TITULO}</h3>
                    <p className="rv-cl-nota">{N2}</p>
                    <Interruptor
                        etiqueta="Información comercial por WhatsApp"
                        icono="message-circle"
                        ayuda={d.comercial.whatsapp ? 'Le enviaremos un mensaje para que lo confirme.' : 'Sin marcar si no te lo ha pedido.'}
                        error={e.whatsapp}
                        valor={d.comercial.whatsapp}
                        onChange={(on) => {
                            fijar('comercial', { ...d.comercial, whatsapp: on });
                        }}
                    />
                    <Interruptor
                        etiqueta="Información comercial por email"
                        icono="mail"
                        ayuda={d.comercial.email ? 'Le enviaremos un mensaje para que lo confirme.' : 'Sin marcar si no te lo ha pedido.'}
                        error={e.comercialEmail}
                        valor={d.comercial.email}
                        onChange={(on) => {
                            fijar('comercial', { ...d.comercial, email: on });
                        }}
                    />
                </>
            )}
        </div>
    );
}
